<template>
    <v-app id="inspire">
        <div class="details-wrap1">
            <div class="details-header">
                <v-layout wrap class="mt-n4">
                    <v-btn class="header-btn-back mb-5" small @click="$router.go(-1)">
                        <v-icon class="header-btn-back-icon">navigate_before</v-icon>
                        Back
                    </v-btn>
                </v-layout>
                <v-layout class="mb-6">
                    <v-flex d-flex xs12 md5>
                        <div>
                            <v-avatar v-if="!no_data.includes(staff)" size="40" :color="!no_data.includes(staff.color) && !no_data.includes(staff.color) ? staff.color : primary" class="mr-2">
                                <v-img v-if="!no_data.includes(staff.avatar)" :src="staff.avatar" :lazy-src="staff.avatar" alt="Staff Avatar" />
                                <span v-else-if="!no_data.includes(staff.first_name) && !no_data.includes(staff.last_name)" class="subtitle-2" style="color: #fff; margin-left: -1px; margin-bottom: -2px;">{{ `${staff.first_name.charAt(0)}${staff.last_name.charAt(0)}`}}</span>
                                <v-icon v-else size="32px" style="color: #fff; margin-left: -0.5px;">account_circle</v-icon>
                            </v-avatar>
                            <v-skeleton-loader
                                v-else-if="loading"
                                class="mr-2"
                                type="avatar"
                            ></v-skeleton-loader>
                        </div>
                        <div>
                            <h2 v-if="!no_data.includes(staff_first_name) && !no_data.includes(staff_last_name)" class="headline mt-1">{{staff_first_name}} {{staff_last_name}}</h2>
                            <v-skeleton-loader
                                v-else-if="loading"
                                style="width: 350px;"
                                type="heading"
                                class="mb-2"
                            ></v-skeleton-loader>
                            <div v-if="!no_data.includes(staffRating)">
                                <v-layout v-if="!no_data.includes(staffRating.averageRate)" class="wrap justify-start align-end">
                                    <span class="text-body-2 black--text mr-1">{{ truncToOneDecimal(staffRating.averageRate) }}</span>
                                    <v-rating
                                        class="d-inline"
                                        v-if="!no_data.includes(staffRating.averageRate)"
                                        :value="roundRating(staffRating.averageRate)"
                                        half-increments
                                        color="primary"
                                        background-color="grey lighten-1"
                                        empty-icon="$ratingFull"
                                        dense
                                        readonly
                                        size="18"
                                    />
                                    <span
                                        v-if="!no_data.includes(staffRating.countRate)"
                                        class="text-body-2 grey--text ml-1">{{ 
                                            `(${staffRating.countRate})`
                                    }}</span>
                                </v-layout>
                                <v-skeleton-loader
                                    v-else-if="staffRating.loadingRate"
                                    style="width: 150px;"
                                    type="text"
                                ></v-skeleton-loader>
                            </div>
                        </div>
                    </v-flex>
                    <v-spacer></v-spacer>
                    <template v-if="!no_data.includes(staff)">
                        <v-btn
                            v-if="staff.status === 'active'"
                            sx12
                            class="mt-0"
                            color="error"
                            dark
                            @click="changeStatusModal(staff)"
                        >
                            <v-icon class="mr-2">remove_circle_outline</v-icon>
                            Deactivate
                        </v-btn>
                        <v-btn
                            v-else
                            sx12
                            class="mt-0"
                            color="success"
                            dark
                            @click="changeStatusModal(staff)"
                        >
                            <v-icon class="mr-2">how_to_reg</v-icon>
                            Activate
                        </v-btn>
                    </template>
                </v-layout>
            </div>
            <v-card class="mt-5">
                <v-layout class="px-4 py-4">
                    <h3>Staff Member Details</h3>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="!no_data.includes(staff)"
                        sx12
                        class="mr-0 mt-0"
                        color="primary"
                        small
                        :disabled="staff.status !== 'active'"
                        @click="editStaffModal(staff)"
                    >
                        <v-icon left>create</v-icon>
                        Edit
                    </v-btn>
                </v-layout>
                <v-divider></v-divider>
                <v-data-table
                        :headers="headers"
                        :items="!no_data.includes(staff) ? [staff] : []"
                        :loading="loading"
                        loader-height="2"
                        class="elevation-1"
                        hide-default-header
                        hide-default-footer
                        mobile-breakpoint="100">
                    <template v-slot:item="{item}">
                        <tr>
                            <td>First name</td>
                            <td>
                                <span v-if="!no_data.includes(item.first_name)">{{item.first_name}}</span>
                                <em v-else class="grey--text">No data</em>
                            </td>
                        </tr>
                        <tr>
                            <td>Middle name</td>
                            <td>
                                <span v-if="!no_data.includes(item.middle_name)">{{item.middle_name}}</span>
                                <em v-else class="grey--text">No data</em>
                            </td>
                        </tr>
                        <tr>
                            <td>Last name</td>
                            <td>
                                <span v-if="!no_data.includes(item.last_name)">{{item.last_name}}</span>
                                <em v-else class="grey--text">No data</em>
                            </td>
                        </tr>
                        <tr>
                            <td>Role</td>
                            <td>
                                <span v-if="!no_data.includes(item.role)">{{ makeCapitalFirstLetter(removeUnderScore(item.role)) }}</span>
                                <em v-else class="grey--text">No data</em>
                            </td>
                        </tr>
                        <tr>
                            <td>Status</td>
                            <td>
                                <span v-if="!no_data.includes(item.status)">
                                    <v-icon :color="setColor(item.status)" style="font-size:13px;">
                                    fiber_manual_record
                                    </v-icon>
                                    {{ makeCapitalFirstLetter(item.status) }}
                                </span>
                                <em v-else class="grey--text">No data</em>
                            </td>
                        </tr>
                        <tr>
                            <td>Email</td>
                            <td>
                                <span v-if="!no_data.includes(item.email)">{{item.email}}</span>
                                <em v-else class="grey--text">No data</em>
                            </td>
                        </tr>
                        <tr>
                            <td>Last Login</td>
                            <td>
                                <span v-if="!no_data.includes(item.last_login)">{{item.last_login}}</span>
                                <em v-else class="grey--text">No data</em>
                            </td>
                        </tr>
                        <tr>
                            <td>Cell Phone</td>
                            <td>
                                <span v-if="!no_data.includes(item.phone)">{{ displayPhone(item.phone) }}</span>
                                <em v-else class="grey--text">No data</em>
                            </td>
                        </tr>
                        <tr>
                            <td>Hire date</td>
                            <td>
                                <span v-if="!no_data.includes(item.hire_date)">{{item.hire_date}}</span>
                                <em v-else class="grey--text">No data</em>
                            </td>
                        </tr>
                        <tr>
                            <td>Profile Picture</td>
                            <td>
                                <v-avatar size="40" :color="!no_data.includes(item.color) ? item.color : primary" class="pointer">
                                    <v-img v-if="!no_data.includes(item.avatar)" :src="item.avatar" :lazy-src="item.avatar" alt="Staff Avatar" />
                                    <span v-else-if="!no_data.includes(item.first_name) && !no_data.includes(item.last_name)" class="subtitle-2" style="color: #fff; margin-left: -1px; margin-bottom: -2px;">{{ `${item.first_name.charAt(0)}${item.last_name.charAt(0)}`}}</span>
                                    <v-icon v-else size="32px" style="color: #fff; margin-left: -0.5px;">account_circle</v-icon>
                                </v-avatar>
                            </td>
                        </tr>
                        <tr>
                            <td>Rating Summary</td>
                            <td>
                                <span v-if="!no_data.includes(staffRating) && !no_data.includes(staffRating.averageRate)">{{
                                    truncToOneDecimal(staffRating.averageRate)
                                }}</span>
                                <em v-else class="grey--text">No data</em>
                            </td>
                        </tr>
                        <tr>
                            <td>Color</td>
                            <td>
                                <v-flex v-if="!no_data.includes(item.color)">
                                    <v-icon :color="item.color" style="font-size:16px;">
                                    fiber_manual_record
                                    </v-icon> {{item.color}}
                                </v-flex>
                                <em v-else class="grey--text">No data</em>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card>

            <template v-if="!no_data.includes(staff) && staff.role !== 'developer'">
                <shift-address :states="states" :staff="staff"></shift-address>

                <staff-ratings-and-comments :staff="staff" :loadingStaff="loading" @done="getDataFromApi" :staffRating="staffRating"></staff-ratings-and-comments>
            </template>

            <v-dialog v-model="changeStatusDialog" max-width="500px" persistent>
                <v-card class="pa-4">

                    <!-- Deactivate Staff -->
                    <template v-if="!no_data.includes(staff) && staff.status === 'active'">
                        <v-card-title class="px-4">
                            <h2 class="title">Deactivate Staff</h2>
                        </v-card-title>
                        <div class="change-status-body px-4 pb-4 pt-2">
                            <div class="text-body-2">Are you sure you want to deactivate staff member?</div>
                            <v-alert
                                class="pa-2 mt-4 mb-0"
                                outlined
                                color="info"
                                icon="lightbulb"
                            >
                                <span class="caption">Before deactivating a staff member, we recommend reassigning 
                                all client locations and jobs to another tech at once. 
                                To do so, please go to Scheduler > Schedule > Actions > Change Tech
                                </span>
                            </v-alert>
                            <v-alert
                                class="pa-2 mt-4"
                                :value="!serverValidation"
                                outlined
                                color="error"
                                icon="priority_high"
                                transition="scale-transition"
                            >
                                <span class="caption">{{ serverValidationMessage }}</span>
                            </v-alert>
                        </div>
                        <v-card-actions class="px-4">
                            <v-spacer></v-spacer>
                            <v-btn color="primary" text @click="closeChangeStatus">Cancel</v-btn>
                            <v-btn
                                color="primary"
                                :loading="changeStatusLoading"
                                :disabled="changeStatusLoading"
                                @click="changeStatus(staff.id, 'non-active')"
                            >Deactivate</v-btn>
                        </v-card-actions>
                    </template>

                    <!-- Activate Staff -->
                    <template v-if="!no_data.includes(staff) && staff.status === 'inactive'">
                        <v-card-title class="px-4">
                            <h2 class="title">Activate Staff</h2>
                        </v-card-title>
                        <div class="change-status-body px-4 pb-4 pt-2">
                            <div class="text-body-2">Are you sure you want to activate staff member?</div>
                            <v-alert
                                class="pa-2 mt-4"
                                :value="!serverValidation"
                                outlined
                                color="error"
                                icon="priority_high"
                                transition="scale-transition"
                            >
                                <span class="caption">{{ serverValidationMessage }}</span>
                            </v-alert>
                        </div>
                        <v-card-actions class="px-4">
                            <v-spacer></v-spacer>
                            <v-btn color="primary" text @click="closeChangeStatus">Cancel</v-btn>
                            <v-btn 
                                color="primary"
                                :loading="changeStatusLoading"
                                :disabled="changeStatusLoading"
                                @click="changeStatus(staff.id, 'active')"
                            >Activate</v-btn>
                        </v-card-actions>
                    </template>

                </v-card>
            </v-dialog>

            <!-- Edit Staff Dialog -->
            <v-dialog v-if="editStaffDialog" v-model="editStaffDialog" max-width="500px" persistent>
                <v-card class="pa-4">
                    <div>
                        <v-card-title>
                            <h2 class="title">Edit Staff Member</h2>
                        </v-card-title>
                        <div class="pa-4">
                            <v-flex class="xs12 px-2">
                                <v-text-field
                                    label="First Name*"
                                    v-model="editStaffData.first_name"
                                    placeholder="Please Enter"
                                    :error-messages="errors($v.editStaffData.first_name) || server_errors.first_name"
                                    @blur="$v.editStaffData.first_name.$touch()"
                                    @focus="resetServerValidation"
                                ></v-text-field>
                            </v-flex>
                            <v-flex class="xs12 px-2">
                                <v-text-field
                                    label="Middle Name"
                                    v-model="editStaffData.middle_name"
                                    placeholder="Please Enter"
                                    :error-messages="server_errors.middle_name"
                                    @focus="resetServerValidation"
                                ></v-text-field>
                            </v-flex>
                            <v-flex class="xs12 px-2">
                                <v-text-field
                                    label="Last Name*"
                                    v-model="editStaffData.last_name"
                                    placeholder="Please Enter"
                                    :error-messages="errors($v.editStaffData.last_name) || server_errors.last_name"
                                    @blur="$v.editStaffData.last_name.$touch()"
                                    @focus="resetServerValidation"
                                ></v-text-field>
                            </v-flex>
                            <v-flex class="xs12 px-2">
                                <v-autocomplete
                                    :items="roles"
                                    label="Role*"
                                    item-text="name"
                                    item-value="value"
                                    v-model="editStaffData.role"
                                    placeholder="Please Select"
                                    :readonly="user_role !== 'owner'"
                                    :disabled="user_role !== 'owner'"
                                    :error-messages="errors($v.editStaffData.role) || server_errors.role"
                                    @blur="$v.editStaffData.role.$touch()"
                                    @focus="resetServerValidation"
                                >
                                </v-autocomplete>
                            </v-flex>
                            <v-flex class="xs12 px-2">
                                <v-text-field
                                    label="Email*"
                                    v-model.trim="editStaffData.email"
                                    :disabled="!['owner', 'manager'].includes(user_role)"
                                    :error-messages="errors($v.editStaffData.email) || server_errors.email"
                                    @blur="$v.editStaffData.email.$touch()"
                                    @focus="resetServerValidation"
                                ></v-text-field>
                            </v-flex>
                            <v-flex class="xs12 px-2">
                                <v-text-field
                                    label="Cell Phone*"
                                    v-model="editStaffData.phone"
                                    v-mask="'(###) ### - ####'"
                                    placeholder="Please Enter"
                                    :error-messages="errors($v.editStaffData.phone) || server_errors.phone"
                                    @blur="$v.editStaffData.phone.$touch()"
                                    @focus="resetServerValidation"
                                ></v-text-field>
                            </v-flex>
                            <v-flex class="xs12 px-2">
                            <v-menu
                                    id="edit_staff_menu_hire_date"   
                                    ref="menu2"
                                    :label="`Hire Date*`"
                                    :close-on-content-click="false"
                                    v-model="menu2"
                                    :nudge-right="40"
                                    placeholder="Please Select"
                                    transition="scale-transition"
                                    offset-y
                                    max-width="290px"
                                    min-width="290px">
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        id="edit_staff_text_hire_date"   
                                        ref="date2"
                                        v-on="on"
                                        v-model="editStaffData.hire_date"
                                        :label="`Hire Date*`"
                                        prepend-icon="event"
                                        :readonly="true"
                                        placeholder="Please Select"
                                        :error-messages="errors($v.editStaffData.hire_date) || server_errors.hire_date"
                                        @blur="$v.editStaffData.hire_date.$touch()"
                                        @focus="resetServerValidation"
                                    ></v-text-field>
                                </template>
                                <v-date-picker
                                    id="edit_staff_picker_hire_date"
                                    v-model="editStaffData.hire_date"
                                    no-title
                                    @input="menu2 = false"
                                >
                                </v-date-picker>
                            </v-menu>
                            </v-flex>
                            <v-flex class="xs12 px-2">
                                <v-menu
                                ref="menu3"
                                :close-on-content-click="false"
                                v-model="menu3"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                max-width="225px"
                                min-width="225px"
                                >
                                <template v-slot:activator="{ on }">
                                    <v-text-field
                                        v-on="on"
                                        label="Color"
                                        v-model="editStaffData.color"
                                        placeholder="Please Enter"
                                        :error-messages="server_errors.color"
                                        @focus="colorPickerFocus(editStaffData.color)"
                                    >
                                        <template slot="prepend">
                                        <v-icon v-on="on" :color="editStaffData.color || '#000000'">brightness_1</v-icon>
                                        </template>
                                    </v-text-field>
                                </template>
                                <v-color-picker
                                    v-if="menu3"
                                    mode="hexa"
                                    v-model="editStaffData.color"
                                    hide-mode-switch
                                    @input="updateColorValue($event, 'editStaffData')"
                                    style="border-bottom-right-radius: 0; border-bottom-left-radius: 0;"
                                ></v-color-picker>
                                <v-card-actions class="white">
                                    <v-spacer></v-spacer>
                                    <v-btn color="primary" text @click="cancelUpdateColor('menu3', 'editStaffData')">Cancel</v-btn>
                                    <v-btn color="primary" @click="menu3 = false">Ok</v-btn>
                                </v-card-actions> 
                                </v-menu>
                            </v-flex>
                            <v-alert class="pa-2 mt-4" :value="$v.editStaffData.$error" outlined color="error" icon="priority_high" transition="scale-transition">
                                <span class="caption">Please fill out form correctly</span>
                            </v-alert>
                            <v-alert class="pa-2 mt-4" :value="!serverValidation" outlined color="error" icon="priority_high" transition="scale-transition">
                                <span class="caption">{{ serverValidationMessage }}</span>
                            </v-alert>
                        </div>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="primary" text @click="closeEditStaff">Cancel</v-btn>
                            <v-btn color="primary" :loading="saveEditStaffLoading" :disabled="saveEditStaffLoading" @click="edit">Save</v-btn>
                        </v-card-actions>
                    </div>
                </v-card>
            </v-dialog>

        </div>
    </v-app>
</template>

<script>
    import ApiService from "@/common/api.service";
    import { mapState, mapActions } from "vuex";
    import { validationMixin } from 'vuelidate'
    import { required, email, helpers } from 'vuelidate/lib/validators'
    import { valMessages, getValMessage } from '@/common/error-messages'
    import { mask } from 'vue-the-mask'
    import ShiftAddress from './ShiftAddress'
    import StaffRatingsAndComments from './StaffRatingsAndComments.vue'

    let validations = {}

    export default {
        mixins: [validationMixin],
        validations() {
            validations = {
                editStaffData: {
                    first_name: { required },
                    last_name: { required },
                    role: { required },
                    email: this.user_role === 'owner' ? { required, email } : {},
                    phone: { required, phone: v => !helpers.req(v) || /^\d{10}$/.test(v.replace(/[()-\s]/g, "")) },
                    hire_date: { required }
                }
            }
            this.errors = getValMessage(valMessages)(validations)
            return validations
        },
        components: {
            ShiftAddress,
            StaffRatingsAndComments
        },
        directives: {mask},
        data() {
            return {
                staff: null,
                menu2: false,
                menu3: false,
                loading: true,
                staff_first_name:'',
                staff_last_name:'',
                staffRating: {
                   averageRate: null,
                   countRate: null,
                   loadingRate: false 
                },
                no_data: [null, "", undefined, "null"],
                startingEditColor: '#000000',
                headers: [
                    {
                        text: 'Staff Member Details',
                        align: 'left',
                        sortable: false,
                        value: 'details',
                        width: '20%'
                    },
                    {
                        text: '',
                        sortable: false
                    }
                ],

                changeStatusDialog: false,
                changeStatusLoading: false,
                editStaffDialog: false,
                statuses: [
                    'Active',
                    'Inactive'
                ],
                selectedStatuses: [
                    'Active',
                    'Inactive'
                ],
                roles: [],
                selectedRoles: [],
                states: [],
                required: v => !!v || 'This field is required',
                email: v => /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(v) || 'E-mail must be valid',
                phone: v => (/^\d{10}$/.test(v) || v === undefined || String(v) === '') || 'Phone must contain 10 digits',
                editStaffData: {
                    first_name: '',
                    middle_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    hire_date: '',
                    role: '',
                    start_address: '',
                    start_zip: '',
                    start_city: '',
                    start_state: '',
                    end_address: '',
                    end_zip: '',
                    end_city: '',
                    end_state: '',
                },

                saveEditStaffLoading: false,
                formValidation: true,
                serverValidation: true,
                serverValidationMessage: 'Oops something went wrong...',
                server_errors: {}
            }
        },
        mounted() {
            this.getDataFromApi()
            this.getRoles();
            this.getStates();
        },
        methods: {
            ...mapActions(["checkAuth", 'vEmit']),
            getDataFromApi(id = this.$route.params.id) {

                this.loading = true
                ApiService.get('/employee/' + id + '/show')
                        .then(response => {
                            this.staff = response.data.staff
                            this.staff.phone = this.justNumbers(this.staff.phone)
                            this.staff_first_name = response.data.staff.first_name;
                            this.staff_last_name = response.data.staff.last_name;
                            this.loading = false;
                        })
            },
            getRoles() {
                ApiService.get('/roles')
                    .then(response => {
                        this.roles = response.data.roles
                        this.selectedRoles = response.data.roles
                    });
            },
            getStates() {
                ApiService.get('/states')
                    .then(response => {
                        this.states = response.data.states;
                    })
            },
            changeStatusModal(item) {
                this.resetServerValidation()
                this.changeStatusLoading = false
                this.changeStatusDialog = true;
            },
            changeStatus(id, status) {
                let data = {
                    id,
                    status
                }
                let successText = '';
                status === 'non-active' ?  successText = 'Staff member has been successfully deactivated.' : successText = 'Staff member has been successfully activated.'
                this.changeStatusLoading = true
                ApiService.post('/employee_status', data)
                    .then(response => {
                        this.getDataFromApi();
                        this.vEmit({ 
                            name: "notification", 
                            data: {
                                status: 'success',
                                text: successText
                            }
                        });
                        this.checkAuth()
                        this.changeStatusLoading = false
                        this.closeChangeStatus()
                    })
                    .catch(({ response }) => {
                        this.changeStatusLoading = false
                        this.serverValidation = false;
                        if (response.data.error) {
                            this.serverValidationMessage = this.titleCase(
                            response.data.error
                            );
                        }
                        if (response.data.message) {
                            this.serverValidationMessage = response.data.message;
                        }
                        if (response.data.errors) {
                            this.server_errors = response.data.errors;
                        }
                    });
            },
            closeChangeStatus(){
                this.changeStatusDialog = false;
            },
            editStaffModal(item) {
                this.$v.$reset()
                this.resetServerValidation()
                Object.assign(this.editStaffData, this.staff);
                this.editStaffData.id = item.id;
                delete this.editStaffData['status'];
                delete this.editStaffData['last_login'];
                this.editStaffDialog = true
            },
            edit() {
                this.resetServerValidation();
                this.$v.editStaffData.$touch();
                if (!this.$v.editStaffData.$invalid) {
                    let requests = [this.editStaf()]
                    if(this.editStaffData.email !== this.staff.email) {
                        requests.push(this.changeEmail())
                    }
                    this.saveEditStaffLoading = true
                    Promise.all(requests)
                    .then(responses => {
                        this.getDataFromApi();
                        this.vEmit({ 
                            name: "notification", 
                            data: {
                                status: 'success',
                                text: 'Changes have been successfully saved.'
                            }
                        })
                        this.saveEditStaffLoading = false
                        this.closeEditStaff()
                    })
                    .catch(({ response }) => {
                        this.saveEditStaffLoading = false
                        this.serverValidation = false
                        if(response.data.error){
                            this.serverValidationMessage = this.titleCase(response.data.error)
                        }
                        if(response.data.message){
                            this.serverValidationMessage = response.data.message
                        }
                        if(response.data.errors) {
                            this.server_errors = response.data.errors
                        }
                    })
                }
            },
            editStaf() {
                return new Promise((resolve, reject) => {
                    let body = _.cloneDeep(this.editStaffData)
                    if(!this.no_data.includes(body.phone)) {
                        body.phone = this.phoneWithoutMask(body.phone)
                    }
                    ApiService.put(`/employee/${this.editStaffData.id}`, body)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    })
                })
            },
            changeEmail () {
                return new Promise((resolve, reject) => {
                    let body = { 
                        staff_id: this.editStaffData.id,
                        current_email: this.staff.email,
                        email: this.editStaffData.email
                    }
                    ApiService.put('/employee/email_change', body)
                    .then(response => {
                        resolve(response)
                    })
                    .catch(error => {
                        reject(error)
                    });
                })
            },
            closeEditStaff() {
                this.editStaffDialog = false;
            },
            setColor(status) {
                if (status == 'active' || status == '1')
                    return 'green';
                return 'red';
            },
            //Remove under score from string
            removeUnderScore(str){
                for(let i in str){
                str = str.replace('_', ' ');
                }
                return str
            },
            makeCapitalFirstLetter(str){
                return str.charAt(0).toUpperCase() + str.slice(1)
            },
            displayPhone (phone) {
                if(/^\d{10}$/.test(phone)) {
                    return `(${phone.substr(0, 3)}) ${phone.substr(3, 3)} - ${phone.substr(6, 4)}`
                } else {
                    return phone
                }
            },
            justNumbers (str) {
                if (typeof str === 'string') {
                    return str.replace(/\D+/g, '')
                } else {
                    return str
                }
            },
            phoneWithoutMask (phone) {
                if(phone && typeof phone === 'string') {
                    return phone.replace(/[()-\s]/g, "")
                } else {
                    return phone
                }
            },
            colorPickerFocus(color) {
                this.startingEditColor = color
                this.resetServerValidation()
            },
            updateColorValue(input, name) {
                this[name].color = input
            },
            cancelUpdateColor(menuName, dataObjectName) {
                this[dataObjectName].color = this.startingEditColor
                this[menuName] = false
            },
            roundRating (i) {
                if (isNaN(Number(i))) { return i }
                if (i.toString().includes('.')) {
                    const wholeNumber = i.toString().split('.')[0]
                    const decimlPart = i.toString().split('.')[1]
                    if (Number(`0.${decimlPart}`) >= 0.5) {
                    return Number(`${wholeNumber}.5`)
                    } else {
                    return Number(wholeNumber)
                    }
                } else {
                    return i
                }
            },
            titleCase(str) {
                return _.upperFirst(str.replace(/_/g, " "));
            },
            truncToOneDecimal (i) {
                if (isNaN(Number(i))) { return i }
                return (Math.floor(Number(i) * 10) / 10).toFixed(1)
            },
            resetServerValidation() {
                this.serverValidation = true
                this.serverValidationMessage = 'Oops something went wrong...'
                this.server_errors = {}
            },
            errors: getValMessage(valMessages)(validations)
        },
        computed: {
            ...mapState({
                user_role: state => state.auth.user.role,
            })
        },
    }
</script>

<style>
/*Remove action controls form table*/
.details-wrap .datatable__actions {
display: none;
}

.upp {
text-transform: uppercase;
}
</style>
